// state/theme.tsx — Theme context with a11y preferences + LIGHT-only mode.
// Task B2. Note: plan listed .ts but JSX requires .tsx (disclosed deviation).
//
// V28 Phase F1 (2026-06-07): LIGHT-ONLY lockdown. The resolved `mode` is now
// hard-pinned to 'light' — no OS detection, no dark/auto resolution. The
// explicit 3-way `preference` state + LS persistence (`asc_theme_v1`) are kept
// so callers/tests still compile and the persisted shape is unchanged, but
// `preference` no longer drives `mode`. `setPreference`/`setMode` stay exported
// as back-compat setters that update the persisted `preference` only; the
// resolved `mode` (and every token consumer) always sees 'light'.

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getTokens, type Tokens, type ThemeMode } from '../tokens';

interface A11yPrefs {
  openDyslexic: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
}

/** What the student chose in Profilo. `auto` = follow the OS preference. */
export type ThemePreference = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  /** Resolved theme actually in effect ('light' | 'dark'). */
  mode: ThemeMode;
  /** Explicit user choice, including 'auto'. Drives the segmented control. */
  preference: ThemePreference;
  /** Set the explicit preference (light / dark / auto). */
  setPreference: (p: ThemePreference) => void;
  /** Back-compat alias: pin the resolved mode (= setPreference for light/dark). */
  setMode: (m: ThemeMode) => void;
  a11y: A11yPrefs;
  setA11y: (updates: Partial<A11yPrefs>) => void;
  tokens: Tokens;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const LS_KEY = 'asc_theme_v1';

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Explicit preference. Restored from LS `pref`; migrated from the legacy
  // `{ userOverride, mode }` shape (override true → that mode; else 'auto').
  // Default theme is LIGHT via 'auto' + light-by-default OS resolution
  // (redesign spec §3.1 / build-contract §C + §D).
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          pref?: ThemePreference;
          userOverride?: boolean;
          mode?: ThemeMode;
        };
        if (parsed.pref === 'light' || parsed.pref === 'dark' || parsed.pref === 'auto') {
          return parsed.pref;
        }
        // Legacy migration: an explicit pre-STEP-3 pick → pin that mode.
        if (parsed.userOverride === true && (parsed.mode === 'light' || parsed.mode === 'dark')) {
          return parsed.mode;
        }
      }
    } catch {
      /* ignore */
    }
    // Founder decision (2026-06-07): default to LIGHT. V28 Phase F1 hard-pins
    // the resolved mode to light app-wide, so this default is the only value a
    // fresh user ever sees rendered; the persisted `preference` is kept for
    // shape compatibility and any future re-enablement.
    return 'light';
  });

  // V28 Phase F1: LIGHT-ONLY lockdown. The resolved mode is hard-pinned to
  // 'light' — no OS detection, no dark/auto resolution. `preference` is still
  // tracked + persisted but no longer drives `mode`.
  const mode: ThemeMode = 'light';

  const setPreference = (p: ThemePreference) => setPreferenceState(p);
  // Back-compat: setMode('light'|'dark') updates the persisted preference.
  // The resolved `mode` stays 'light' regardless (light-only lockdown).
  const setMode = (m: ThemeMode) => setPreferenceState(m);

  const [a11y, setA11yState] = useState<A11yPrefs>(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) try {
      const parsed = JSON.parse(stored) as { a11y?: Partial<A11yPrefs> };
      return { openDyslexic: false, highContrast: false, reducedMotion: false, ...parsed.a11y };
    } catch (_) { /* ignore */ }
    return { openDyslexic: false, highContrast: false, reducedMotion: false };
  });

  useEffect(() => {
    // Persist the resolved mode (always 'light' under F1) + a11y + the explicit
    // preference. `userOverride` is kept for backward-compat readers (true iff
    // the user pinned a non-auto preference).
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ mode, a11y, userOverride: preference !== 'auto', pref: preference }),
    );
    document.documentElement.dataset.theme = mode;
    document.documentElement.dataset.openDyslexic = String(a11y.openDyslexic);
    document.documentElement.dataset.highContrast = String(a11y.highContrast);
    document.documentElement.dataset.reducedMotion = String(a11y.reducedMotion);
  }, [mode, a11y, preference]);

  const tokens = getTokens(mode);

  return (
    <ThemeContext.Provider value={{
      mode,
      preference,
      setPreference,
      setMode,
      a11y,
      setA11y: (u) => setA11yState((prev) => ({ ...prev, ...u })),
      tokens,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTokens(): Tokens {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTokens must be used inside ThemeProvider');
  return ctx.tokens;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
